// ---------------------------------------------------------------------------
// InputDrawerV3 — drawer do input no Inputs V3.
// ---------------------------------------------------------------------------
// Estrutura: header fixo + toolbar de ações rápidas + corpo (rail lateral
// + pane ativa). As panes são componentes independentes na pasta panes/.
//
// Ações na toolbar:
//   - Enviar arquivo (primária). Durante upload, botão fica disabled e
//     um ThreeDots aparece na área de hint (padrão: spinner oficial da
//     aplicação, posicionado em local estratégico sem alterar layout do
//     botão — cf. design-system).
//   - Usar mês anterior (DA-003): só aparece quando o input tem
//     ``reuse_allowed=true`` no catálogo. Copia automaticamente o
//     último arquivo enviado em mês anterior em estado
//     success/warning e dispara o pipeline. Mesmo handler que o
//     "Usar arquivo" da aba "Entregas anteriores", sem precisar
//     escolher origem (backend resolve a última automaticamente).
//   - Reprocessar (DEV-only): re-executa parse + cross-links + blocos
//     dependentes sobre o arquivo já existente no mês.
//   - Baixar (menu — agora vive dentro da timeline da NowPane após
//     A-014): arquivo enviado, arquivo validado.
//
// Mantém as métricas de geometria do drawer V2 (ocupa quase todo o
// viewport vertical, margem para a direita, cantos arredondados), mas
// é alargado para acomodar o rail + timeline. Ver DRAWER_WIDTH em
// ./styles.js.
// ---------------------------------------------------------------------------

import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation, Trans } from "react-i18next";
import { ThreeDots } from "react-loader-spinner";

import CloudUpload from "@material-ui/icons/CloudUpload";
import DateRange from "@material-ui/icons/DateRange";
import FlashOn from "@material-ui/icons/FlashOn";
import MenuBook from "@material-ui/icons/MenuBook";
import Repeat from "@material-ui/icons/Repeat";
import CompareArrows from "@material-ui/icons/CompareArrows";
import People from "@material-ui/icons/People";
import History from "@material-ui/icons/History";
import Build from "@material-ui/icons/Build";
import Switch from "@material-ui/core/Switch";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tooltip from "@material-ui/core/Tooltip";

import useDrawerStyles from "./styles";
import {
  setWaiveInputV3,
  unsetWaiveInputV3,
} from "../../../../services/inputsV3Service";
import { toast } from "react-toastify";
import useThemeColors from "../../../../hooks/useThemeColors";
import useFileDropZone from "../hooks/useFileDropZone";
import DrawerHeader from "./DrawerHeader";
import DrawerRail from "./DrawerRail";
import NowPane from "./panes/NowPane";
import AboutPane from "./panes/AboutPane";
import InputInfoPane from "./panes/InputInfoPane";
import ResponsiblesPane from "./panes/ResponsiblesPane";
import CrossLinksPane from "./panes/CrossLinksPane";
import HistoryPane from "./panes/HistoryPane";
import AdminActionsPane from "./panes/AdminActionsPane";
import { useDrawerDetailsV3 } from "./useDrawerDetailsV3";
import { getAcceptExtensions } from "../../inputs_components/utils/fileTypes";
import { isMultiMonthEligible } from "../../../../services/inputsV3Volatility";
import MultiMonthUploadModal from "../multi_month_upload";

// ``buildRailItems`` agora deriva as flags do rail a partir do
// ``notesState`` REAL (mesmo ``useInputNotesV3`` consumido pelos
// panes).
//
// Reorganizacao 2026-04-28 (rev 2026-05-28):
//   TRILHA            → este mes, entregas anteriores
//   SOBRE ESTE INPUT  → instrucoes
//   CONFIGURACAO      → regras de reuso, responsaveis, cruzamentos
//
// A aba "Mensagens" foi removida: tudo que ela mostrava (notes do
// pipeline, perguntas pendentes, erros bloqueantes) ja vive na
// timeline de "Este mes" — duplicar canal so confunde o consultor
// ("menos e mais"). O ``NowPane`` ganhou uma tag no topo quando ha
// acao pendente (reenvio / responder pergunta), entao a navegacao
// continua descobrivel.
const buildRailItems = (input, notesState, decisionsState, userRole, t) => {
  const notes = notesState?.notes || [];
  const decisions = decisionsState?.decisions || [];
  const errCount = notes.filter((n) => n.severity === 3).length;
  const warnCount = notes.filter((n) => n.severity === 2).length;
  const hasPendingDecision = decisions.length > 0;
  const items = [
    {
      id: "now",
      label: t("modeling_inputs_v3.rail.tab_now"),
      icon: FlashOn,
      group: "trail",
      badgeDot:
        errCount > 0
          ? "critical"
          : warnCount > 0 || hasPendingDecision
            ? "warning"
            : undefined,
    },
    {
      id: "history",
      label: t("modeling_inputs_v3.rail.tab_history"),
      icon: History,
      group: "trail",
    },
    {
      id: "input_info",
      label: t("modeling_inputs_v3.rail.tab_instructions"),
      icon: MenuBook,
      group: "about_input",
    },
    {
      id: "about",
      label: t("modeling_inputs_v3.rail.tab_reuse_rules"),
      icon: Repeat,
      group: "config",
    },
    {
      id: "responsibles",
      label: t("modeling_inputs_v3.rail.tab_responsibles"),
      icon: People,
      group: "config",
    },
    {
      id: "xlinks",
      label: t("modeling_inputs_v3.rail.tab_xlinks"),
      icon: CompareArrows,
      group: "config",
    },
  ];
  // ADM group: destructive ops (delete upload, reprocess). DEV-only.
  if (userRole === "DEV") {
    items.push({
      id: "actions",
      label: t("modeling_inputs_v3.rail.tab_actions"),
      icon: Build,
      group: "adm",
    });
  }
  return items;
};

const PANES = {
  now: NowPane,
  input_info: InputInfoPane,
  about: AboutPane,
  responsibles: ResponsiblesPane,
  xlinks: CrossLinksPane,
  history: HistoryPane,
  actions: AdminActionsPane,
};

// Download option builders — labels are deferred to t() at call time.
const makeDownloadOriginal = (t, { disabled = false, disabledHint } = {}) => ({
  kind: "original",
  label: t("modeling_inputs_v3.download.original_label"),
  hint: disabledHint || t("modeling_inputs_v3.download.original_hint"),
  disabled,
});
const makeDownloadCleaned = (t, { disabled = false, disabledHint } = {}) => ({
  kind: "cleaned_up",
  label: t("modeling_inputs_v3.download.cleaned_label"),
  hint: disabledHint || t("modeling_inputs_v3.download.cleaned_hint"),
  disabled,
});

// Deriva as opcoes de download condizentes com o que esta disponivel
// de fato para o input neste mes. A regra:
//   - Sem entrega (``!hasDelivered``): menu vazio. O botao proprio ja
//     fica desabilitado pelo chamador, mas preservamos o contrato
//     "opcoes condizentes com o disponivel".
//   - Arquivo rejeitado (phase ``blocked`` ou ``system_error``): o
//     pipeline nao chegou a produzir a versao "validada" — so
//     existe o original que o usuario mandou. Expor "Arquivo
//     validado" nesse estado seria mentir.
//   - Demais casos (ok, warning, permanent, pending_decision): ambas
//     as versoes estao disponiveis.
//
// ``phase`` vem do runtime V3 (``derivePhase`` do adapter).
//
// ``sensitive``: inputs com dados sensiveis nao guardam o arquivo
// original (ele e' purgado apos a anonimizacao). Mantemos a opcao
// "original" visivel mas DESABILITADA (opacidade + sem click) — o
// usuario entende que existe mas nao e' acessivel.
//
// ``processedAvailable``: a versao processada (parquet canonico -> xlsx)
// so existe quando houve parse OK com DataFrame no mes. Quando ausente,
// mantemos a opcao "Arquivo validado" visivel porem DESABILITADA (mesmo
// padrao do original sensivel) em vez de deixar o usuario clicar e
// receber erro.
function deriveDownloadOptions(
  hasDelivered,
  phase,
  t,
  { sensitive, processedAvailable } = {},
) {
  if (!hasDelivered) return [];
  const original = makeDownloadOriginal(
    t,
    sensitive
      ? {
          disabled: true,
          disabledHint: t(
            "modeling_inputs_v3.download.original_sensitive_hint",
          ),
        }
      : undefined,
  );
  const cleaned = makeDownloadCleaned(
    t,
    processedAvailable
      ? undefined
      : {
          disabled: true,
          disabledHint: t(
            "modeling_inputs_v3.download.cleaned_unavailable_hint",
          ),
        },
  );
  if (phase === "blocked" || phase === "system_error") {
    // Parse nao produziu versao validada — so o original faz sentido.
    return [original];
  }
  return [original, cleaned];
}

const InputDrawerV3 = ({
  open,
  input,
  period,
  appName,
  year,
  month,
  onClose,
  isUploading,
  busyAction,
  isDownloading,
  onUpload,
  onReplicate,
  onReprocess,
  onDownload,
  onRuntimeChange,
  fileInputRef,
  monthlyStatus,
  selectedMonthIndex,
  onSelectMonth,
  // Edicao de responsaveis (rev. 2026-04-29). Originadas em
  // ``InputsV3`` e propagadas ate o ``ResponsiblesPane``. Sem essas
  // props o pane cai automaticamente em modo somente-leitura.
  usersList,
  userRole,
  onSaveResponsibles,
  isSavingResponsibles,
  selectStyles,
  // Onda 2.0 (mai/2026): payload de ``drawer_details`` vindo do hook
  // unico ``useInputsStateV3`` no parent. Quando presente, o drawer
  // NAO dispara mais ``useDrawerDetailsV3`` proprio — fonte de
  // verdade vem 100% do polling consolidado, eliminando o atraso
  // observado pelo usuario ("drawer aguardando para iniciar leitura
  // enquanto cockpit ja indicava cruzamento rodando"). Shape igual
  // ao retorno do ``useDrawerDetailsV3`` (loading, about, control,
  // currentMonthUploads, previousMonthUpload, notes, answeredDecisions,
  // decisions, crossLinks, auditRecent, runtimeInput, monthStatus,
  // error, refresh).
  drawerDetailsOverride,
  // "Marcar como analisado" de um warning (sev=2) da timeline. Vem de
  // ``InputsV3.handleMarkNoteVerifiedV3`` (pause/POST/resume) e e'
  // repassado ao ``NowPane`` -> caixas amarelas. ``(noteId, verified)``
  // -> Promise.
  onMarkNoteVerified,
  // Drag & drop: corpo do drawer (tudo abaixo do header) vira area
  // droppable. ``onFileDrop(file)`` envia o arquivo para o input deste
  // drawer; quando ausente, a area NAO e' droppable (gate de permissao
  // no parent). ``onDropReject(reasonKey)`` notifica pasta/nao-arquivo.
  onFileDrop,
  onDropReject,
}) => {
  const { clientColor } = useThemeColors();
  const classes = useDrawerStyles({ clientColor });
  const { t } = useTranslation();

  // Drop zone do corpo do drawer. Desabilitada durante upload em curso
  // (single-upload) e quando o parent nao forneceu ``onFileDrop``.
  const drawerDroppable = typeof onFileDrop === "function" && !isUploading;
  const { isOver: isDrawerDragOver, dropHandlers: drawerDropHandlers } =
    useFileDropZone({
      onFile: onFileDrop,
      onReject: onDropReject,
      disabled: !drawerDroppable,
    });
  const [activeTab, setActiveTab] = useState("now");
  const [multiMonthOpen, setMultiMonthOpen] = useState(false);
  // Onda 3.4 (CCU Testes, mai/2026): trava o fechamento do drawer
  // (ESC, click no backdrop) enquanto a chamada da API de resposta
  // de D-22 esta em curso. Propagado por DecisionsCard ->
  // NowPane.onDecisionSubmittingChange.
  const [decisionSubmitting, setDecisionSubmitting] = useState(false);
  const [instructionsSuggesting, setInstructionsSuggesting] = useState(false);

  // PA-007 / Phase 4.2: estado consolidado vive AQUI (no drawer), e e
  // populado por UMA SO chamada ao endpoint
  // ``GET /api/v3/inputs/<app>/<y>/<m>/<meta>/drawer-details/``. Esse
  // endpoint substitui o conjunto fragmentado anterior (notes,
  // decisions, cross-links, history, about, audit) — ver
  // ``InputsV3DrawerDetailsView`` no backend, otimizado para <=4
  // queries SQL. O drawer permanece montado entre trocas de aba, entao
  // os panes consomem o ``drawerDetails`` direto via prop sem
  // re-fetch.
  const metaName = input?.meta_name || input?.input_id || null;
  const lastRuntimeSyncRef = useRef(null);
  const overrideActive = !!drawerDetailsOverride;
  // Mantemos o hook montado mesmo em modo override para nao quebrar
  // chamadores legados que ainda nao migraram. ``enabled=false``
  // significa "nao faz fetch" — o hook devolve o ``EMPTY`` initial
  // state e o ``refresh`` vira no-op. Assim o resto do componente
  // continua usando ``drawerDetails.X`` normalmente; a fonte muda
  // sem riscar a API interna.
  const drawerDetailsFetched = useDrawerDetailsV3({
    appName,
    year,
    month,
    metaName,
    isUploading,
    enabled: !overrideActive && !!open,
  });
  const rawDrawerDetails = overrideActive
    ? drawerDetailsOverride
    : drawerDetailsFetched;
  const drawerDetails = useMemo(() => {
    if (!isUploading) return rawDrawerDetails;
    return {
      ...rawDrawerDetails,
      loading: false,
      notes: [],
      decisions: [],
      crossLinks: [],
      runtimeInput: rawDrawerDetails.runtimeInput
        ? {
            ...rawDrawerDetails.runtimeInput,
            control_state: "uploaded",
            attention_label: null,
            cross_linking: null,
            xlinks_in_flight: 0,
            stages: {
              ...(rawDrawerDetails.runtimeInput.stages || {}),
              // Onda 3.1: stages internos passaram a usar
              // ``processing`` (era ``running``).
              data_structure: "processing",
              cross_linking: "waiting",
            },
          }
        : null,
      control: rawDrawerDetails.control
        ? { ...rawDrawerDetails.control, state: "uploaded" }
        : { state: "uploaded" },
    };
  }, [isUploading, rawDrawerDetails]);
  const visibleInput = useMemo(() => {
    if (!isUploading || !input) return input;
    return {
      ...input,
      control_state: "uploaded",
      phase: "processing",
      attention_label: null,
      cross_linking: null,
      xlinks_in_flight: 0,
      stages: {
        ...(input.stages || {}),
        // Onda 3.1: stages internos passaram a usar ``processing``
        // (era ``running``).
        data_structure: "processing",
        cross_linking: "waiting",
      },
    };
  }, [input, isUploading]);
  useEffect(() => {
    lastRuntimeSyncRef.current = null;
  }, [metaName, year, month]);

  // Onda 1.4: ``activeTab`` agora reseta ao trocar de input/mes para
  // garantir UX previsivel — antes, abrir input A em "history", fechar,
  // abrir input B trazia B ja em "history" (o estado persiste por
  // instancia de componente). Resetar para "now" alinha com o sentido
  // dominante de uso (acompanhar o estado atual do input recem aberto).
  useEffect(() => {
    if (open && metaName) setActiveTab("now");
  }, [metaName, year, month, open]);

  useEffect(() => {
    if (!open || !onRuntimeChange || !metaName || !drawerDetails.runtimeInput) {
      return;
    }
    const runtimeMetaName = drawerDetails.runtimeInput.meta_name || null;
    if (runtimeMetaName !== metaName) return;
    // Onda 1.4: syncKey deterministico baseado em campos discretos.
    // Antes, ``JSON.stringify`` da arvore inteira de ``runtimeInput``
    // gerava uma string custosa de calcular E suscetivel a mudar mesmo
    // quando NADA visivel mudou (ordem de chaves em dicts aninhados,
    // timestamps de ``updated_at``/``last_run_at`` que oscilam por
    // milissegundos a cada poll, etc.). O efeito era disparar
    // ``onRuntimeChange`` desnecessariamente, propagando re-render
    // pra ``InputsV3`` e cascateando re-render dos cards. Agora a
    // chave inclui SO o que importa pro consumidor: estado, label de
    // atencao, decisoes pendentes, hash do timestamp da ultima
    // entrega, e o status do mes.
    const ri = drawerDetails.runtimeInput;
    const syncKey = [
      metaName,
      year,
      month,
      ri.control_state || "",
      ri.attention_label || "",
      ri.is_permanent_for_month ? 1 : 0,
      ri.has_pending_decision ? 1 : 0,
      ri.delivered_at || "",
      ri.updated_in || "",
      ri.stages?.data_source || "",
      ri.stages?.data_structure || "",
      ri.stages?.cross_linking || "",
      ri.xlinks_in_flight || 0,
      drawerDetails.monthStatus || "",
    ].join("|");
    if (lastRuntimeSyncRef.current === syncKey) return;
    lastRuntimeSyncRef.current = syncKey;
    onRuntimeChange({
      metaName,
      year,
      month,
      runtimeInput: drawerDetails.runtimeInput,
      monthStatus: drawerDetails.monthStatus,
    });
  }, [
    open,
    onRuntimeChange,
    metaName,
    year,
    month,
    drawerDetails.runtimeInput,
    drawerDetails.monthStatus,
  ]);
  // Shapes derivados que reproduzem o contrato dos antigos hooks
  // ``useInputNotesV3``/``useInputDecisionsV3`` — assim os panes
  // (NowPane, DecisionsCard) continuam funcionando sem mudar a API.
  const notesState = {
    loading: drawerDetails.loading,
    notes: drawerDetails.notes,
    control: drawerDetails.control,
    answeredDecisions: drawerDetails.answeredDecisions,
    error: drawerDetails.error,
    refresh: drawerDetails.refresh,
  };
  const decisionsState = {
    decisions: drawerDetails.decisions,
    loading: drawerDetails.loading,
    error: drawerDetails.error,
    refresh: drawerDetails.refresh,
  };

  // UX de um clique: o botão "Enviar arquivo" abre o file picker
  // oculto; ao escolher o arquivo, o upload dispara automaticamente.
  // Mantemos o input file controlado pela ref do parent (fileInputRef)
  // para que `handleUploadFileClick` continue lendo
  // `fileInputRef.current.files[0]` sem mudança de API.
  const handleOpenFilePicker = () => {
    if (isUploading) return;
    if (fileInputRef && fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };
  const handleFileChosen = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (onUpload) onUpload();
  };

  // Waive de input opcional ("Marcar como concluido"). Estado local
  // controla APENAS o switch visual (otimismo localizado) e o
  // spinner. NAO propagamos o otimismo para o parent (calendar /
  // inputbox / contadores) nem para o NowPane (3 quadros + timeline)
  // antes do retorno da API: a UI deve refletir o estado real assim
  // que o backend responde. Razao: o otimismo "global" anterior
  // gerava lixo visual durante o roundtrip (ex.: switch-off mantendo
  // "arquivo entregue por Jairo" na timeline porque o
  // ``drawerDetails`` ainda nao tinha sido refrescado).
  //
  // Fluxo agora:
  //   1. ``setWaiveOptimistic(desired)`` => switch fica "vivo"
  //      instantaneamente, ``effectiveWaived`` reflete a intencao.
  //   2. ``setWaiveBusy(true)`` => spinner ao lado + fade sobre
  //      stages/timeline no NowPane (via prop ``isWaivePending``).
  //      Bloqueia reentrada.
  //   3. API POST/DELETE.
  //   4. Sucesso: ``drawerDetails.refresh()`` busca dados reais; o
  //      ``useEffect`` que observa ``drawerDetails.runtimeInput`` ja
  //      existente (linha ~232) propaga ``onRuntimeChange`` com o
  //      runtime REAL. O ``waiveOptimistic`` reseta quando
  //      ``input?.is_waived`` muda no parent.
  //   5. Erro: reverte ``setWaiveOptimistic(null)`` (switch volta);
  //      nao ha nada para reverter no parent (nao propagamos).
  const [waiveBusy, setWaiveBusy] = useState(false);
  const [waiveOptimistic, setWaiveOptimistic] = useState(null);
  // Reset do estado otimista quando o ``input.is_waived`` real muda
  // (ex.: snapshot recarregou e o backend convergiu).
  useEffect(() => {
    setWaiveOptimistic(null);
  }, [input?.is_waived, input?.meta_name, year, month]);

  // Override otimista da config de reuso. A AboutPane atualiza o
  // toggle local instantaneamente, mas a toolbar ("Usar mes anterior")
  // e a HistoryPane ("Usar arquivo") leem ``reuse_allowed``/
  // ``reuse_auto`` de ``drawerDetails.about`` (servidor). Sem este
  // override, ligar/desligar o reuso so refletiria nesses botoes
  // quando o re-fetch do polling resolvesse — atraso perceptivel
  // (ate 30s quando o input esta "parado"). A AboutPane chama
  // ``onReuseConfigApplied`` ASSIM QUE A API RETORNA; guardamos as
  // flags aqui (keyed por meta_name) e elas tem precedencia sobre o
  // ``about`` ate o servidor convergir, quando descartamos o override.
  const [reuseOverride, setReuseOverride] = useState(null);
  useEffect(() => {
    setReuseOverride(null);
  }, [metaName, year, month]);
  useEffect(() => {
    if (!reuseOverride) return;
    const about = drawerDetails?.about;
    if (!about || about.meta_name !== reuseOverride.metaName) return;
    if (
      !!about.reuse_allowed === !!reuseOverride.reuseAllowed &&
      !!about.reuse_auto === !!reuseOverride.reuseAuto
    ) {
      setReuseOverride(null);
    }
  }, [drawerDetails, reuseOverride]);
  const handleReuseConfigApplied = (next) => {
    if (!next || !next.metaName) return;
    setReuseOverride({
      metaName: next.metaName,
      reuseAllowed: !!next.reuseAllowed,
      reuseAuto: !!next.reuseAuto,
    });
  };

  // Ao abrir, sempre come\u00e7a em "Este m\u00eas" \u2014 \u00e9 onde mora a a\u00e7\u00e3o.
  // I-14 (auditoria mai/2026): adicionadas ``year`` e ``month`` \u00e0s deps.
  // Antes, se o usu\u00e1rio mudasse de m\u00eas com o drawer aberto e na aba
  // "Hist\u00f3rico" (ou outra), a aba ficava ativa para o m\u00eas novo \u2014 o
  // contexto da aba era do m\u00eas anterior. Reset agora dispara em
  // qualquer mudan\u00e7a de identidade input/per\u00edodo.
  useEffect(() => {
    if (open) setActiveTab("now");
  }, [open, input?.input_id, year, month]);

  // Fecha com ESC (quando não está fazendo upload nem respondendo D-22).
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (
        e.key === "Escape" &&
        !isUploading &&
        !decisionSubmitting &&
        !instructionsSuggesting
      )
        onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, isUploading, decisionSubmitting, instructionsSuggesting, onClose]);

  // Animacao de entrada (replica o ProgressDrawerV3, que ja desliza
  // suavemente porque NUNCA desmonta — o drawer fica em ``open=false``
  // com ``transform: translateX(100%)``). Aqui o ``InputDrawerV3``
  // tem ``if (!open && !input) return null`` mais abaixo, entao na
  // PRIMEIRA abertura ele monta ja com ``open=true`` e a classe
  // ``drawerOpen`` aplicada — sem estado anterior para a CSS
  // transition partir. Resultado: aparecia "do nada".
  //
  // Solucao: ``visualOpen`` segue ``open`` mas com 1 frame de
  // atraso (rAF). Assim, no primeiro paint o drawer tem
  // ``drawer`` (translateX(100%)) e no proximo frame entra
  // ``drawerOpen`` (translateX(0)) — a transition CSS roda.
  const [visualOpen, setVisualOpen] = useState(false);
  useEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => setVisualOpen(true));
      return () => cancelAnimationFrame(id);
    }
    setVisualOpen(false);
    return undefined;
  }, [open]);

  if (!open && !input) return null;

  const items = buildRailItems(
    visibleInput,
    notesState,
    decisionsState,
    userRole,
    t,
  );
  const ActivePane = PANES[activeTab] || NowPane;
  const hasDelivered = !!visibleInput?.updated_in;
  const isInitialLoading = !!notesState?.loading;

  // Status do pill no DrawerHeader pode estar momentaneamente DESALINHADO
  // com o periodo atual logo apos uma troca de mes: o parent
  // (``InputsV3``) seta ``inputDrawerInput`` com o objeto clicado da
  // grid, que pode estar estale por ~200ms enquanto ``inputsStatus``
  // refaz o fetch para o novo mes (ja vimos: a grid intencionalmente
  // mantem dados antigos para nao piscar vazio durante refresh). Sem
  // este escudo, o usuario abre o drawer no input X em mes B (yellow)
  // e ve o pill verde do mes A por uma fracao de segundo.
  //
  // Defesa: quando ``drawerDetails`` ainda nao recebeu ``runtimeInput``
  // do periodo corrente (loading=true sem runtime ainda), sinalizamos
  // ``statusLoading`` para o ``DrawerHeader``, que entao renderiza o
  // pill em modo "Carregando..." (cinza neutro) ao inves de pintar com
  // a cor stale do ``input`` prop. Quando o runtime chega, o sync ja
  // existente em ``InputsV3`` propaga o estado correto, ``statusLoading``
  // vira false, e o pill assume a cor real.
  const runtimeMatchesPeriod =
    !!drawerDetails.runtimeInput &&
    drawerDetails.runtimeInput.meta_name === metaName;
  const statusLoading = drawerDetails.loading || !runtimeMatchesPeriod;
  // ``downloadOptions``/``canDownload`` continuam aqui porque o
  // ``ActivePane`` (NowPane) consome o array para renderizar o botao
  // "Baixar" embutido na timeline (A-014). A logica em si nao mudou.
  const downloadOptions = deriveDownloadOptions(
    hasDelivered,
    visibleInput?.phase,
    t,
    {
      sensitive: !!drawerDetails?.about?.sensitive,
      processedAvailable: !!drawerDetails?.about?.processed_available,
    },
  );

  // Botao "Usar mes anterior" (DA-003): aparece somente se o input
  // tem ``reuse_allowed=true`` no catalogo (ImplementationInputV3) e
  // a replicacao automatica nao esta habilitada. Quando ``reuse_auto``
  // esta ligado, a acao manual da toolbar fica redundante.
  // Desabilitado apenas durante operacao em curso (upload/loading) —
  // ja existir arquivo no mes NAO bloqueia mais (substituir e
  // permitido; rev. 2026-05-29, simetria com "Usar arquivo").
  // Em ``onClick`` chama o mesmo handler que a HistoryPane usa — o
  // backend resolve automaticamente o ultimo periodo anterior em
  // ``state in (success, warning)`` e copia para o mes atual.
  const aboutFromDetails = drawerDetails?.about || null;
  const detailsReuseLoaded =
    !!aboutFromDetails &&
    aboutFromDetails.meta_name === visibleInput?.meta_name;
  // Override otimista (set em ``handleReuseConfigApplied`` quando a API
  // de salvar a config retorna) tem precedencia ate o ``about`` do
  // servidor convergir. Cobre tambem a janela de loading: enquanto o
  // override existe, ``reuseConfigLoaded`` ja vale true.
  const overrideMatches =
    !!reuseOverride && reuseOverride.metaName === visibleInput?.meta_name;
  const reuseConfigLoaded = detailsReuseLoaded || overrideMatches;
  const reuseAllowed = overrideMatches
    ? !!reuseOverride.reuseAllowed
    : detailsReuseLoaded && !!aboutFromDetails.reuse_allowed;
  const reuseAuto = overrideMatches
    ? !!reuseOverride.reuseAuto
    : detailsReuseLoaded && !!aboutFromDetails.reuse_auto;
  // Botao "Multiplos meses": gateado APENAS por volatility (apenas
  // ``monthly`` — onde cada mes recebe um arquivo potencialmente
  // DIFERENTE; ex-``semi_fixed`` foi consolidado em ``monthly`` pela
  // migration 0035 / rev. 2026-05-28, e continua elegivel porque
  // cada slot mensal e' independente). Singleton, yearly e
  // carry_forward NAO entram: la uma unica entrega ja cobre
  // multiplos meses por design (janela infinita / FY / open-ended).
  //
  // Nota (2026-05): a condicao ``!reuseAuto`` foi removida por
  // pedido do usuario — mesmo com reutilizacao automatica ligada,
  // o consultor pode querer subir explicitamente um arquivo
  // (possivelmente diferente) para varios meses de uma vez.
  // Ver ``services/inputsV3Volatility.js``.
  const volatility = visibleInput?.volatility || null;
  const multiMonthEligible = isMultiMonthEligible(volatility);
  const multiMonthButtonVisible = multiMonthEligible;
  // Rev. 2026-05-29: ``hasDelivered`` foi REMOVIDO do gating. Antes, ter
  // um arquivo no mes corrente desabilitava "Usar mes anterior" (regra
  // "replicate so faz sentido sobre periodo pending"). Por decisao de
  // produto, reutilizar/substituir mesmo com entrega existente passa a
  // ser permitido — alinhando com a HistoryPane ("Usar arquivo"), que
  // nunca checou ``hasDelivered``. Continua bloqueado apenas durante
  // operacoes em curso (upload/carga inicial).
  const replicateDisabled = isUploading || isInitialLoading;
  const replicateTitle = isInitialLoading
    ? t("modeling_inputs_v3.toolbar.loading_info")
    : t("modeling_inputs_v3.toolbar.replicate_default_title");

  const isOptional = !!visibleInput?.optional;
  const isWaivedReal = !!visibleInput?.is_waived;
  const effectiveWaived =
    waiveOptimistic !== null ? waiveOptimistic : isWaivedReal;
  // O switch nao faz sentido enquanto ja existe arquivo no mes —
  // mesma regra do backend (``waive_not_applicable``). Tambem
  // bloqueamos durante upload/loading.
  //
  // IMPORTANTE: NAO incluir ``waiveBusy`` aqui. Se o switch ficasse
  // ``disabled`` durante o roundtrip da API, o MUI aplica a paleta
  // "desativada" (cor cinza/morta) sobre o estado checked — o
  // usuario percebe como "demorou para colorir". Mantemos
  // habilitado e protegemos a reentrada no proprio handler com um
  // early-return em ``waiveBusy``. O spinner ao lado ja sinaliza
  // o processo em curso.
  const waiveDisabled = isUploading || isInitialLoading || hasDelivered;
  // Toggle do switch "Marcar como concluido".
  //
  // Politica: NAO propagar otimismo para o parent (``onRuntimeChange``)
  // antes do retorno da API. Apenas o switch local responde imediato
  // (via ``waiveOptimistic``). O NowPane recebe ``isWaivePending`` e
  // mostra um overlay sobre stages + timeline para evitar exibir
  // dados antigos durante o roundtrip.
  //
  // No sucesso, ``drawerDetails.refresh()`` busca o estado REAL e o
  // useEffect (linha ~232) propaga via ``onRuntimeChange`` com o
  // runtime verdadeiro — UI principal converge automaticamente,
  // ``waiveOptimistic`` reseta pelo useEffect que observa
  // ``input?.is_waived``.
  const handleToggleWaive = async () => {
    if (waiveDisabled || waiveBusy) return;
    const desired = !effectiveWaived;
    // 1) Switch responde imediato (sem tocar no parent / drawer).
    setWaiveOptimistic(desired);
    setWaiveBusy(true);
    try {
      const fn = desired ? setWaiveInputV3 : unsetWaiveInputV3;
      const data = await fn({
        appName,
        year,
        month,
        metaName: input?.meta_name || input?.input_id,
      });
      if (data?.success === false) {
        // Falha "soft": reverte switch e mostra toast. Como nao
        // tocamos no parent/drawer, nao ha nada mais para reverter.
        setWaiveOptimistic(null);
        toast(
          desired
            ? t("modeling_inputs_v3.waive_toasts.error_mark")
            : t("modeling_inputs_v3.waive_toasts.error_unmark"),
          { position: "top-right", type: "error", autoClose: 4000 },
        );
      } else {
        // 2) Sucesso: invalida cache de sincronia para garantir que a
        //    proxima propagacao do runtime real chegue ao parent
        //    mesmo se o ``syncKey`` coincidir (raro mas possivel).
        lastRuntimeSyncRef.current = null;
        if (drawerDetails?.refresh) drawerDetails.refresh();
        toast(
          desired
            ? t("modeling_inputs_v3.waive_toasts.success_mark")
            : t("modeling_inputs_v3.waive_toasts.success_unmark"),
          { position: "top-right", type: "success", autoClose: 2500 },
        );
      }
    } catch {
      setWaiveOptimistic(null);
      toast(
        desired
          ? t("modeling_inputs_v3.waive_toasts.fail_mark")
          : t("modeling_inputs_v3.waive_toasts.fail_unmark"),
        { position: "top-right", type: "error", autoClose: 4000 },
      );
    } finally {
      setWaiveBusy(false);
    }
  };

  return (
    <>
      <div
        className={`${classes.overlay} ${visualOpen ? classes.overlayOpen : ""}`}
        onClick={() => {
          if (isUploading || decisionSubmitting || instructionsSuggesting)
            return;
          onClose();
        }}
      />
      <aside
        className={`${classes.drawer} ${visualOpen ? classes.drawerOpen : ""}`}
        role="dialog"
        aria-label={t("modeling_inputs_v3.toolbar.dialog_aria", {
          name: visibleInput?.display_name || "",
        })}
      >
        <DrawerHeader
          input={visibleInput}
          period={period}
          onClose={() => {
            if (isUploading || decisionSubmitting || instructionsSuggesting)
              return;
            onClose();
          }}
          monthlyStatus={monthlyStatus}
          selectedMonthIndex={selectedMonthIndex}
          onSelectMonth={onSelectMonth}
          statusLoading={statusLoading}
          reuseOptions={{
            loaded: reuseConfigLoaded,
            allowed: reuseAllowed,
            auto: reuseAuto,
          }}
        />

        <div
          className={classes.drawerDropWrap}
          {...(drawerDroppable ? drawerDropHandlers : {})}
        >
          {isDrawerDragOver && (
            <div className={classes.drawerDropOverlay} aria-hidden="true">
              <CloudUpload className={classes.drawerDropIcon} />
              <span className={classes.drawerDropText}>
                {t("inputs_v3.drop.hint")}
              </span>
            </div>
          )}
          <div className={classes.drawerToolbar}>
            <input
              ref={fileInputRef}
              type="file"
              accept={getAcceptExtensions(
                input?.expected_file_types ?? input?.expected_file_type,
              )}
              onChange={handleFileChosen}
              style={{ display: "none" }}
            />
            <button
              type="button"
              className={`${classes.btn} ${classes.btnPrimary}`}
              onClick={handleOpenFilePicker}
              disabled={isUploading || isInitialLoading}
              title={
                isInitialLoading
                  ? t("modeling_inputs_v3.toolbar.loading_info")
                  : undefined
              }
            >
              <CloudUpload />
              {t("modeling_inputs_v3.toolbar.submit_file")}
            </button>
            {/* Botao "Multiplos meses" — visivel apenas para inputs
              cuja volatility e' mensal (``monthly``). Para
              yearly/singleton/carry_forward o conceito nao se
              aplica (uma entrega cobre o periodo inteiro). */}
            {multiMonthButtonVisible && (
              <button
                type="button"
                className={`${classes.btn} ${classes.btnGhost}`}
                onClick={() => setMultiMonthOpen(true)}
                disabled={isUploading || isInitialLoading}
                title={t("modeling_inputs_v3.toolbar.multi_month_btn_title")}
              >
                <DateRange />
                {t("modeling_inputs_v3.toolbar.multi_month_btn")}
              </button>
            )}
            {isOptional && !hasDelivered && (
              <>
                <span className={classes.toolbarOr} aria-hidden="true">
                  {t("modeling_inputs_v3.toolbar.or_separator")}
                </span>
                <Tooltip
                  title={t("modeling_inputs_v3.toolbar.waive_tooltip")}
                  placement="bottom"
                  arrow
                >
                  <span className={classes.toolbarWaive}>
                    <Switch
                      size="small"
                      checked={effectiveWaived}
                      onChange={handleToggleWaive}
                      disabled={waiveDisabled}
                      color="primary"
                      inputProps={{
                        "aria-label": t(
                          "modeling_inputs_v3.toolbar.waive_aria",
                        ),
                      }}
                    />
                    {t("modeling_inputs_v3.toolbar.waive_label")}
                    {waiveBusy && (
                      <CircularProgress
                        size={14}
                        thickness={5}
                        className={classes.toolbarWaiveSpinner}
                        aria-label={t("modeling_inputs_v3.toolbar.saving_aria")}
                      />
                    )}
                  </span>
                </Tooltip>
              </>
            )}
            {reuseAllowed && !reuseAuto && onReplicate && (
              <button
                type="button"
                className={`${classes.btn} ${classes.btnGhost}`}
                onClick={() => onReplicate()}
                disabled={replicateDisabled}
                title={replicateTitle}
              >
                <History />
                {t("modeling_inputs_v3.toolbar.replicate_btn")}
              </button>
            )}
            {/* Leva 97: o botão "Reprocessar" foi movido para a aba
              "Ações" do grupo ADM (gateado por DEV). Antes vivia
              aqui no toolbar — saindo de cima da fronteira do drawer
              quando o usuário não era DEV (botão escondido) e
              poluindo a toolbar para quem era. Agrupar todas as ações
              administrativas (delete + reprocess + reset month) numa
              mesma aba evita esse vai-e-vem. */}
            {/* A-014: o botao "Baixar" foi movido para DENTRO da timeline,
              logo apos o evento "Arquivo recebido" — passando a viver
              ao lado do contexto que ele baixa. O ``downloadBtnRef`` /
              ``downloadAnchor`` desta toolbar sao preservados como
              referencia historica mas nao renderizam mais botao
              proprio aqui. O ``DownloadMenu`` de fato agora e
              instanciado pela ``NowPane`` (no item de upload). */}
            <div className={classes.toolbarSpacer} />
            {isUploading ? (
              <span
                className={classes.toolbarHint}
                style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
              >
                {busyAction === "reprocess"
                  ? t("modeling_inputs_v3.toolbar.busy_reprocess")
                  : busyAction === "replicate"
                    ? t("modeling_inputs_v3.toolbar.busy_replicate")
                    : t("modeling_inputs_v3.toolbar.busy_uploading")}
                <ThreeDots
                  color={clientColor}
                  height="10px"
                  width="32px"
                  wrapperStyle={{ height: "10px", width: "32px" }}
                />
              </span>
            ) : isDownloading ? (
              <span
                className={classes.toolbarHint}
                style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
              >
                {t("modeling_inputs_v3.toolbar.preparing_download")}
                <ThreeDots
                  color={clientColor}
                  height="10px"
                  width="32px"
                  wrapperStyle={{ height: "10px", width: "32px" }}
                />
              </span>
            ) : hasDelivered ? (
              <span className={classes.toolbarHint}>
                <Trans
                  i18nKey="modeling_inputs_v3.toolbar.last_delivery"
                  values={{ date: formatDate(input.updated_in) }}
                  components={{ 1: <strong /> }}
                />
              </span>
            ) : (
              <span className={classes.toolbarHint}>
                {t("modeling_inputs_v3.toolbar.nothing_sent_yet")}
              </span>
            )}
          </div>

          <div className={classes.drawerBody}>
            <DrawerRail
              items={items}
              activeId={activeTab}
              onSelect={setActiveTab}
            />
            <div className={classes.content}>
              <ActivePane
                input={visibleInput}
                onDownload={onDownload}
                onReplicate={onReplicate}
                onReprocess={onReprocess}
                isUploading={isUploading}
                busyAction={busyAction}
                isDownloading={isDownloading}
                downloadOptions={downloadOptions}
                appName={appName}
                year={year}
                month={month}
                notesState={notesState}
                decisionsState={decisionsState}
                drawerDetails={drawerDetails}
                usersList={usersList}
                userRole={userRole}
                onSaveResponsibles={onSaveResponsibles}
                isSavingResponsibles={isSavingResponsibles}
                selectStyles={selectStyles}
                reuseConfigLoaded={reuseConfigLoaded}
                reuseAllowed={reuseAllowed}
                reuseAuto={reuseAuto}
                onReuseConfigApplied={handleReuseConfigApplied}
                hasDelivered={hasDelivered}
                isWaivePending={waiveBusy}
                onDecisionSubmittingChange={setDecisionSubmitting}
                onInstructionsSuggestingChange={setInstructionsSuggesting}
                onMarkNoteVerified={onMarkNoteVerified}
              />
            </div>
          </div>
        </div>
      </aside>
      {/* Modal "Enviar multiplos meses" — vive como sibling do drawer
          pra herdar o overlay proprio (z-index 1400) e poder ser
          dispensado com ESC sem fechar o drawer. */}
      {multiMonthButtonVisible && (
        <MultiMonthUploadModal
          open={multiMonthOpen}
          onClose={() => setMultiMonthOpen(false)}
          appName={appName}
          input={input}
          availableMonths={(monthlyStatus || []).map((m) => ({
            year: m.year,
            month: m.month,
          }))}
          anchorYear={year}
          anchorMonth={month}
          onUploaded={() => {
            // Refresh dos detalhes do drawer apos cada bateria de
            // uploads — o snapshot global do parent ja se atualiza
            // via polling, mas o drawer detail e o melhor candidato
            // a mostrar a entrega que o usuario acabou de fazer.
            if (drawerDetails?.refresh) drawerDetails.refresh();
          }}
        />
      )}
    </>
  );
};

// Formata uma data (``YYYY-MM-DD`` ou ISO completo) como ``dd/mm/yyyy``.
//
// Importante: ``new Date("YYYY-MM-DD")`` e parseado como meia-noite UTC
// pelo JS; em fusos negativos (GMT-3 Brasil, por exemplo) isso exibe
// a data ANTERIOR. Como ``updated_in`` do adapter e sempre a parte
// ``YYYY-MM-DD`` do ``delivered_at``, cai exatamente nesse bug. Por
// isso tratamos esse caso manualmente: partimos a string e montamos
// sem envolver ``new Date()``. Para ISO completo (``...T...Z``) o
// caminho padrao continua valido.
function formatDate(val) {
  if (!val) return "";
  const str = String(val);
  const ymdMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymdMatch && !str.includes("T")) {
    const [, yyyy, mm, dd] = ymdMatch;
    return `${dd}/${mm}/${yyyy}`;
  }
  try {
    const d = new Date(str);
    if (Number.isNaN(d.getTime())) return str;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch (_) {
    return str;
  }
}

InputDrawerV3.propTypes = {
  open: PropTypes.bool.isRequired,
  input: PropTypes.object,
  period: PropTypes.string,
  appName: PropTypes.string,
  year: PropTypes.number,
  month: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  isUploading: PropTypes.bool,
  busyAction: PropTypes.oneOf(["upload", "replicate", "reprocess"]),
  isDownloading: PropTypes.bool,
  onUpload: PropTypes.func,
  onReplicate: PropTypes.func,
  onReprocess: PropTypes.func,
  onDownload: PropTypes.func,
  onRuntimeChange: PropTypes.func,
  monthlyStatus: PropTypes.array,
  selectedMonthIndex: PropTypes.number,
  onSelectMonth: PropTypes.func,
  fileInputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  usersList: PropTypes.array,
  userRole: PropTypes.string,
  onSaveResponsibles: PropTypes.func,
  isSavingResponsibles: PropTypes.bool,
  selectStyles: PropTypes.object,
  drawerDetailsOverride: PropTypes.shape({
    loading: PropTypes.bool,
    about: PropTypes.object,
    control: PropTypes.object,
    currentMonthUploads: PropTypes.array,
    previousMonthUpload: PropTypes.object,
    notes: PropTypes.array,
    answeredDecisions: PropTypes.array,
    decisions: PropTypes.array,
    crossLinks: PropTypes.array,
    auditRecent: PropTypes.array,
    runtimeInput: PropTypes.object,
    monthStatus: PropTypes.string,
    error: PropTypes.object,
    refresh: PropTypes.func,
  }),
  onMarkNoteVerified: PropTypes.func,
  onFileDrop: PropTypes.func,
  onDropReject: PropTypes.func,
};

export default InputDrawerV3;
